<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'

const props = defineProps({
  map: {
    type: Object,
    required: true,
  },
  id: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    required: true,
    validator: (value) =>
      ['geojson', 'vector', 'raster', 'image', 'video'].includes(value),
  },
  data: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  options: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  url: {
    type: String,
    required: false,
    default: '',
  },
  coordinates: {
    type: Array,
    required: false,
    default: () => [],
  },
})

const isAdded = ref(false)

const addSource = () => {
  if (!props.map.getSource(props.id)) {
    // Only allow valid source keys per Mapbox spec; drop invalid ones like "layout"
    const allowedKeys = [
      'type',
      // geojson
      'data', 'maxzoom', 'attribution', 'buffer', 'tolerance', 'cluster', 'clusterRadius',
      'clusterMaxZoom', 'lineMetrics', 'generateId', 'promoteId',
      // vector
      'url', 'tiles', 'minzoom', 'scheme',
      // raster & raster-dem
      'tileSize', 'bounds', 'encoding'
    ]

    const source = { type: props.type }
    Object.entries(props.options || {}).forEach(([key, val]) => {
      if (allowedKeys.includes(key)) {
        source[key] = val
      }
    })

    if (props.type === 'geojson') {
      source.data = props.data
      source.promoteId = 'code'
    }

    if (props.type === 'raster' || props.type === 'image') {
      source.url = props.url
      source.coordinates = props.coordinates
    }
    props.map.addSource(props.id, source)
    isAdded.value = true
  }
}

const removeSource = () => {
  if (props.map && props.map.getSource && props.map.getSource(props.id)) {
    props.map.removeSource(props.id)
  }
}

onMounted(() => {
  addSource()
  props.map.on('style.load', addSource)
})

onUnmounted(() => {
  removeSource()
  props.map.off('style.load', addSource)
})

watch(() => props.data, (newValue, oldValue) => {
  if (!isAdded.value) return
  const newLen = newValue && newValue.features ? newValue.features.length : undefined
  const oldLen = oldValue && oldValue.features ? oldValue.features.length : undefined
  if (newLen !== oldLen) {
    const src = props.map.getSource(props.id)
    if (src && typeof src.setData === 'function' && props.data) {
      src.setData(props.data)
    }
  }
})
</script>

<template>
  <slot v-if="isAdded"></slot>
</template>

